import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import api from '../../../services/api';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PageContainer from '../../../common/components/PageContainer';
import useAlerta from '../../../common/components/useAlerta';
import AppSnackbar from '../../../common/components/AppSnackbar';

const CadastroMedicao = () => {
  const { register, handleSubmit, reset } = useForm();
  const { alerta, mostrarAlerta, fecharAlerta } = useAlerta();
  const [propostas, setPropostas] = useState([]);
  const [listaDeItens, setListaDeItens] = useState([]);
  const [novoItem, setNovoItem] = useState({ descricao: '', quantidade: '', unidade: '', preco: '' });
  const [mtrs, setMtrs] = useState([]);
  const [novoMtr, setNovoMtr] = useState('');

  useEffect(() => {
    const fetchPropostas = async () => {
      try {
        const response = await api.get('/medicao/proposta');
        setPropostas(response.data);
      } catch (error) {
        console.error('Erro ao buscar propostas:', error);
      }
    };
    fetchPropostas();
  }, []);

  const adicionarItem = () => {
    const { descricao, quantidade, unidade, preco } = novoItem;
    if (!descricao || !quantidade || !unidade || !preco) {
      mostrarAlerta('Preencha todos os campos do item!', 'error');
      return;
    }

    const quantidadeNum = parseFloat(quantidade);
    const precoNum = parseFloat(preco);
    const total = quantidadeNum * precoNum;

    const itemFormatado = {
      descricao,
      quantidade: quantidadeNum,
      unidade,
      preco: precoNum,
      total
    };

    setListaDeItens([...listaDeItens, itemFormatado]);
    setNovoItem({ descricao: '', quantidade: '', unidade: '', preco: '' });
  };

  const removerItem = (index) => {
    const novaLista = [...listaDeItens];
    novaLista.splice(index, 1);
    setListaDeItens(novaLista);
  };

  const adicionarMtr = () => {
    if (!novoMtr.trim()) {
      mostrarAlerta('Digite um código de MTR!', 'error');
      return;
    }
    setMtrs([...mtrs, { codigo: novoMtr.trim() }]);
    setNovoMtr('');
  };

  const removerMtr = (index) => {
    const novaLista = [...mtrs];
    novaLista.splice(index, 1);
    setMtrs(novaLista);
  };

  const onSubmit = async (data) => {
    if (listaDeItens.length === 0) {
      mostrarAlerta('Adicione pelo menos um item à lista!', 'error');
      return;
    }

    const formData = {
      idProposta: data.proposta,
      dataMobilizacao: data.dataMobilizacao,
      dataColeta: data.dataColeta,
      itens: listaDeItens,
      mtrs: mtrs
    };

    try {
      const response = await api.post('/medicao', formData, { responseType: 'blob' });
      mostrarAlerta('Medição cadastrada com sucesso!', 'success');
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      reset();
      setListaDeItens([]);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Erro ao cadastrar medição:', error);
      mostrarAlerta('Erro ao cadastrar medição!', 'error');
    }
  };

  return (
    <PageContainer sx={{ maxWidth: 900 }}>
      <Paper component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: { xs: 2, sm: 4 } }}>
        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
          Medição
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField {...register('proposta')} select label="Proposta" defaultValue="" required fullWidth>
              <MenuItem value="">Selecione uma proposta</MenuItem>
              {propostas.map((prop) => (
                <MenuItem key={prop.id} value={prop.id}>{prop.codigo}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField {...register('dataAtividade')} label="Data da atividade" type="date" InputLabelProps={{ shrink: true }} required fullWidth />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Adicionar MTR
        </Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            label="Código do MTR"
            value={novoMtr}
            onChange={(e) => setNovoMtr(e.target.value)}
            fullWidth
          />
          <Button variant="outlined" onClick={adicionarMtr} sx={{ flexShrink: 0 }}>Adicionar</Button>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2, gap: 1 }}>
          {mtrs.map((mtr, index) => (
            <Chip key={index} label={mtr.codigo} onDelete={() => removerMtr(index)} />
          ))}
        </Stack>

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Adicionar Item
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField label="Descrição" value={novoItem.descricao} onChange={(e) => setNovoItem({ ...novoItem, descricao: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Quantidade" type="number" inputProps={{ step: '0.01' }} value={novoItem.quantidade} onChange={(e) => setNovoItem({ ...novoItem, quantidade: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Unidade" value={novoItem.unidade} onChange={(e) => setNovoItem({ ...novoItem, unidade: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField label="Preço Unitário" type="number" inputProps={{ step: '0.01' }} value={novoItem.preco} onChange={(e) => setNovoItem({ ...novoItem, preco: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button variant="contained" color="secondary" fullWidth sx={{ height: '100%' }} onClick={adicionarItem}>Adicionar</Button>
          </Grid>
        </Grid>

        {listaDeItens.length > 0 && (
          <TableContainer sx={{ mt: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Quantidade</TableCell>
                  <TableCell>Unidade</TableCell>
                  <TableCell>Preço</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listaDeItens.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.descricao}</TableCell>
                    <TableCell>{item.quantidade}</TableCell>
                    <TableCell>{item.unidade}</TableCell>
                    <TableCell>R$ {item.preco}</TableCell>
                    <TableCell align="right">R$ {item.total.toFixed(2)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => removerItem(index)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button type="submit" variant="contained" color="secondary" size="large" sx={{ px: 6 }}>
            Salvar Medição
          </Button>
        </Box>
      </Paper>

      <AppSnackbar alerta={alerta} onClose={fecharAlerta} />
    </PageContainer>
  );
};

export default CadastroMedicao;
